import type React from 'react';
import { ArrowRight, ChartColumn, Clock, Presentation, RotateCw, Users } from 'lucide-react';
import { LiveBadge } from '@components/live';
import { PollQuestionBlock } from '@components/polls/PollQuestionBlock';
import { SHARE_HIT_AREA_44, ShareButton } from '@components/ui/share-button';
import { livePicksAt, withOwnChange, type LiveAnswering } from '@lib/livePolls/useLiveAnswer';
import { useTapSettle } from '@lib/livePolls/useTapSettle';
import type { LiveMe, LiveQuestion, LiveState } from '@models/api';
import type { PollRead } from '@models/api/poll';
import { PlaceLogo } from './bigScreen/BigScreenParts';
import { LIVE_POLL_COPY } from './livePollCopy';

export interface LivePollCardProps {
  state: LiveState;
  me: LiveMe | null;
  /** Answering (the sheet's `/answer`, a member card's vote route). Left out: read only. */
  answering?: LiveAnswering;
  /** Share the live link: drawn for the author alone, from Start (`me.is_author`). The feed card's byline has its own. */
  onShare?: () => void;
  /**
   * D1's link row under the waiting card: "While you wait, see what people in {Space} are looking for". Given only
   * when the viewer can open that Space's feed (the sheet decides); left out, no row.
   */
  whileYouWait?: { spaceName: string; onOpen: () => void };
  /**
   * Given on a feed card only: ended, it shows `FEED_RESULTS_SHOWN` questions and a "See all {n} questions" link that
   * calls this, the same open as a tap on the card. The sheet, `/live/{code}` and the post page leave it out: all.
   */
  onSeeAll?: () => void;
  /** The focus-return mark for the link (`postSheetOpenerProps`), as the card's opener carries it. */
  seeAllProps?: React.HTMLAttributes<HTMLElement>;
  /** In a Post sheet (D2): the count reads 12/16 500 mb-gray-600 before an answer too; the feed card's (L6) is quieter. */
  inSheet?: boolean;
}

/**
 * One live question as the poll block TECH-197's `PollQuestionBlock` draws (a live poll keeps the tick and
 * retries, `onFailure: 'keep-and-retry'`). Counts are public on a live poll (`/state`); the card shows the bars
 * only once this viewer answered the open question (TECH-867 § Decided "One count").
 */
const asPollBlock = (question: LiveQuestion, myOptionIds: string[], closed: boolean): PollRead => ({
  mode: 'live',
  live_state: closed ? 'ended' : 'live',
  current_position: question.position,
  questions_count: 1,
  allows_multiple: question.allows_multiple,
  hide_voter_names: true,
  closes_at: null,
  end_set_as: null,
  closed_at: null,
  is_closed: closed,
  voters_count: question.voters_count,
  options: question.options.map(option => ({ ...option })),
  my_option_ids: myOptionIds,
  my_answer_held: false,
  results_visible: true,
  can_see_voters: false,
  can_edit: false,
  can_end: false,
  can_reopen: false,
  live_code: null,
});

/** "Sat 8 Nov", as L8 writes it (the viewer's own clock and zone). */
const endedDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    .format(date)
    .replace(',', '');
};

const LiveTopRow: React.FC<{ children: React.ReactNode; onShare?: () => void }> = ({
  children,
  onShare,
}) => (
  <div className="flex min-h-8 items-center justify-between gap-2">
    <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>
    {onShare ? (
      <ShareButton
        ariaLabel={LIVE_POLL_COPY.shareLabel}
        className={SHARE_HIT_AREA_44}
        onClick={event => {
          event.stopPropagation();
          onShare();
        }}
      />
    ) : null}
  </div>
);

/** The waiting card (D1) and the "Get ready" card (L6b, D1c): a title and a line, no options. */
const LiveNotice: React.FC<{
  top: React.ReactNode;
  title: string;
  line: string;
  big: boolean;
  onShare?: () => void;
  footer?: React.ReactNode;
}> = ({ top, title, line, big, onShare, footer }) => (
  <div className={big ? 'flex flex-col gap-2.5 py-3' : 'flex flex-col gap-2 py-2'}>
    <LiveTopRow onShare={onShare}>{top}</LiveTopRow>
    <h2
      className={
        big
          ? 'font-livvic text-[26px] leading-[30px] font-bold tracking-[-0.01em] text-mb-ink'
          : 'font-livvic text-2xl leading-7 font-bold text-mb-ink'
      }
    >
      {title}
    </h2>
    <p
      className={
        big
          ? 'text-base leading-[22px] text-mb-gray-dark'
          : 'text-[15px] leading-[21px] text-mb-gray-dark'
      }
    >
      {line}
    </p>
    {footer}
  </div>
);

/** D1's quiet link row (live-D1 values: top border, 15/20 500 teal ink, arrow 18). */
const WhileYouWaitRow: React.FC<{ spaceName: string; onOpen: () => void }> = ({
  spaceName,
  onOpen,
}) => (
  <button
    type="button"
    onClick={event => {
      event.stopPropagation();
      onOpen();
    }}
    className="flex min-h-11 w-full items-center gap-2 border-t border-mb-border pt-1 text-left text-[15px] leading-5 font-medium text-mb-teal-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink"
  >
    <span className="min-w-0 flex-1">{LIVE_POLL_COPY.whileYouWait(spaceName)}</span>
    <ArrowRight aria-hidden="true" className="size-[18px] shrink-0" strokeWidth={2} />
  </button>
);

/** The place's logo and name on the waiting and Get ready cards, so the card loses nothing when the host starts. */
const LivePlaceRow: React.FC<{ place: LiveState['place'] }> = ({ place }) => (
  <div className="flex items-center gap-3 pb-1">
    <PlaceLogo
      name={place.name}
      imageUrl={place.image_url}
      className="size-18 rounded-2xl"
      initialsClassName="text-[28px] leading-8"
    />
    <p className="line-clamp-2 min-w-0 font-livvic text-[22px] leading-[26px] font-bold wrap-break-word text-mb-ink">
      {place.name}
    </p>
  </div>
);

const AnswersCount: React.FC<{ count: number; strong: boolean }> = ({ count, strong }) => (
  <span
    className={
      strong
        ? 'flex items-center gap-1.5 text-xs leading-4 font-medium text-mb-dark-gray'
        : 'flex items-center gap-1.5 text-xs leading-4 font-normal text-mb-grey-300'
    }
  >
    <Users aria-hidden="true" className="size-3.5 shrink-0 text-mb-dark-gray" strokeWidth={2} />
    {LIVE_POLL_COPY.answers(count)}
  </span>
);

/** The open question (L6, L7; D2, D3, D4; E2). */
const LiveQuestionView: React.FC<{
  state: LiveState;
  question: LiveQuestion;
  me: LiveMe | null;
  answering?: LiveAnswering;
  onShare?: () => void;
  inSheet?: boolean;
}> = ({ state, question: read, me, answering, onShare, inSheet = false }) => {
  // The bars move the moment this viewer taps (founder, 3 Nov), not at the next read.
  const question = withOwnChange(read, answering?.ownChange);
  const position = question.position;
  const failed = answering?.failed?.position === position ? answering.failed : null;
  const picks = failed ? failed.optionIds : livePicksAt(me, position);
  const answered = picks.length > 0 && failed === null;
  const canAnswer = answering !== undefined && me?.can_answer !== false;
  const poll = asPollBlock(question, picks, false);
  const failedAt = failed?.optionIds.at(-1) ?? null;
  const stateLine = question.allows_multiple
    ? LIVE_POLL_COPY.questionPickMany(position + 1, state.questions_count)
    : LIVE_POLL_COPY.questionPickOne(position + 1, state.questions_count);

  return (
    <div className="flex flex-col gap-3" data-testid="live-poll-card" data-state="live">
      <PollQuestionBlock
        mode="card"
        onFailure="keep-and-retry"
        poll={poll}
        rows={answered ? 'result-open' : 'live'}
        topLine={
          <LiveTopRow onShare={onShare}>
            <LiveBadge />
            <span className="text-xs leading-4 font-medium text-mb-dark-gray">{stateLine}</span>
          </LiveTopRow>
        }
        question={question.text}
        onToggleOption={canAnswer ? answering.toggle : undefined}
        afterOption={optionId =>
          failed && optionId === failedAt ? (
            <li className="list-none">
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  answering?.retry();
                }}
                // mb-error-text #B91C1C (no token in tailwind.css).
                className="flex min-h-11 items-center gap-1.5 text-sm leading-[18px] font-medium text-[#B91C1C] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink"
              >
                <RotateCw aria-hidden="true" className="size-4 shrink-0" strokeWidth={2} />
                {LIVE_POLL_COPY.didntSend}
              </button>
            </li>
          ) : null
        }
      />
      <div className="flex items-center justify-between gap-3">
        <AnswersCount count={question.voters_count} strong={answered || inSheet} />
        {answered ? null : (
          <span className="shrink-0 text-right text-xs leading-4 text-mb-grey-300">
            {LIVE_POLL_COPY.answerToSeeResults}
          </span>
        )}
      </div>
      {answered ? (
        <p className="text-xs leading-4 text-mb-dark-gray">{LIVE_POLL_COPY.answerIsIn}</p>
      ) : null}
    </div>
  );
};

/** How many questions an ended live poll's feed card shows before "See all" (founder, 3 Nov). */
const FEED_RESULTS_SHOWN = 2;

/**
 * Ended (L8): every question shown, or on a feed card the first 2 and "See all {n} questions" (`onSeeAll`), own picks
 * as B3 (RESOLUTIONS L6), "Ended {date}", "{n} people answered".
 */
const LiveResultsView: React.FC<
  Pick<LivePollCardProps, 'state' | 'me' | 'onShare' | 'onSeeAll' | 'seeAllProps'>
> = ({ state, me, onShare, onSeeAll, seeAllProps }) => {
  const results = state.results ?? [];
  const short = onSeeAll !== undefined && results.length > FEED_RESULTS_SHOWN;
  const shown = short ? results.slice(0, FEED_RESULTS_SHOWN) : results;
  const ended = endedDate(state.closed_at);
  return (
    <div className="flex flex-col gap-3" data-testid="live-poll-card" data-state="ended">
      <LiveTopRow onShare={onShare}>
        <span className="flex items-center gap-1.5 text-xs leading-4 font-semibold text-mb-gray-dark">
          <Presentation
            aria-hidden="true"
            className="size-3.5 shrink-0 text-mb-teal-ink"
            strokeWidth={2.25}
          />
          {LIVE_POLL_COPY.finalResults(results.length)}
        </span>
      </LiveTopRow>
      <div className="flex flex-col gap-4">
        {shown.map(question => (
          <PollQuestionBlock
            key={question.position}
            mode="card"
            onFailure="keep-and-retry"
            poll={asPollBlock(question, livePicksAt(me, question.position), true)}
            rows={question.voters_count > 0 ? 'result-closed' : 'closed-empty'}
            question={LIVE_POLL_COPY.numberedQuestion(question.position + 1, question.text)}
          />
        ))}
      </div>
      {short ? (
        <button
          type="button"
          {...seeAllProps}
          onClick={event => {
            // The card's own click would open it a second time.
            event.stopPropagation();
            onSeeAll();
          }}
          className="flex min-h-11 w-full items-center text-left text-[15px] leading-5 font-medium text-mb-teal-ink underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mb-teal-ink"
        >
          {LIVE_POLL_COPY.seeAllQuestions(results.length)}
        </button>
      ) : null}
      <div className="flex items-center justify-between gap-3 text-xs leading-4">
        <span className="flex min-w-0 items-center gap-1.5 font-medium text-mb-dark-gray">
          <Clock
            aria-hidden="true"
            className="size-3.5 shrink-0 text-mb-dark-gray"
            strokeWidth={2}
          />
          {ended ? LIVE_POLL_COPY.endedOn(ended) : null}
        </span>
        {state.voters_count !== null ? (
          <span className="shrink-0 text-right font-normal text-mb-dark-gray">
            {LIVE_POLL_COPY.peopleAnswered(state.voters_count)}
          </span>
        ) : null}
      </div>
    </div>
  );
};

/**
 * The live poll card (TECH-867 § Views, Polls page L6–L8; D1, D1c, D2–D4), drawn from `/state` and `/me` in
 * the Post sheet and from `GET /questions/{id}/live` on a member's feed card. Its states: not started (D1's
 * waiting card), started before question 1 (L6b, D1c), a question open (L6/D2; bars once this viewer answered
 * it, L7/D3; a question reopened by Back keeps the answer, F7), ended (L8). No "{n} here" count anywhere and no
 * "See who" (TECH-867 § Decided "One count").
 */
export const LivePollCard: React.FC<LivePollCardProps> = ({
  state,
  me,
  answering,
  onShare,
  whileYouWait,
  onSeeAll,
  seeAllProps,
  inSheet = false,
}) => {
  // Share is the author's alone (founder, 3 Nov), from Start (F6): hidden on the waiting card.
  const share = state.live_state === 'not_started' || !me?.is_author ? undefined : onShare;
  // What is shown: an answer tap within 500 ms of a change was aimed at what stood there before (the waiting card,
  // or the last question), so it is dropped (LOOPB-867 review).
  const tapSettled = useTapSettle(
    `${state.live_state}|${state.question ? state.question.position : 'none'}`,
  );
  const settledAnswering: LiveAnswering | undefined = answering && {
    ...answering,
    toggle: optionId => {
      if (tapSettled()) answering.toggle(optionId);
    },
  };

  if (state.live_state === 'ended')
    return (
      <LiveResultsView
        state={state}
        me={me}
        onShare={share}
        onSeeAll={onSeeAll}
        seeAllProps={seeAllProps}
      />
    );

  if (state.live_state === 'not_started') {
    return (
      <div data-testid="live-poll-card" data-state="not_started">
        <LivePlaceRow place={state.place} />
        <LiveNotice
          big
          top={
            <span className="flex items-center gap-1.5 text-[13px] leading-[18px] font-medium text-mb-dark-gray">
              <ChartColumn
                aria-hidden="true"
                className="size-3.5 shrink-0 text-mb-teal-ink"
                strokeWidth={2.25}
              />
              {LIVE_POLL_COPY.cardStartingSoon(state.questions_count)}
            </span>
          }
          title={LIVE_POLL_COPY.hangTight}
          line={LIVE_POLL_COPY.keepThisOpen}
          footer={whileYouWait ? <WhileYouWaitRow {...whileYouWait} /> : undefined}
        />
      </div>
    );
  }

  if (!state.question) {
    return (
      <div data-testid="live-poll-card" data-state="get-ready">
        <LivePlaceRow place={state.place} />
        <LiveNotice
          big={false}
          onShare={share}
          top={
            <>
              <LiveBadge />
              <span className="text-[13px] leading-[18px] font-medium text-mb-dark-gray">
                {LIVE_POLL_COPY.eyebrowQuestions(state.questions_count)}
              </span>
            </>
          }
          title={LIVE_POLL_COPY.getReady}
          line={LIVE_POLL_COPY.questionOneComingUpLine}
        />
      </div>
    );
  }

  return (
    <LiveQuestionView
      state={state}
      question={state.question}
      me={me}
      answering={settledAnswering}
      onShare={share}
      inSheet={inSheet}
    />
  );
};
